import React from "react"

/**
 * Reusable Table component with Tailwind CSS styling
 *
 * @param {Object} props
 * @param {Array} props.data - Array of data objects to display in the table
 * @param {Array} props.columns - Array of column configuration objects
 * @param {number|string} props.height - Height of the table component in pixels or 'auto'
 * @param {string} props.title - Optional title for the table
 * @param {Function} props.onRowClick - Optional handler for row click events
 * @param {string} props.emptyMessage - Message to display when no data is available
 * @param {boolean} props.showFooter - Whether to show a footer with action buttons (deprecated)
 * @param {Function} props.onViewMore - Handler for the "View More Details" button (deprecated)
 * @returns {JSX.Element}
 */
const Table = ({
	data = [],
	columns = [],
	height = 300,
	title = "",
	onRowClick,
	emptyMessage = "No data available",
	showFooter = false, // Kept for backward compatibility
	onViewMore // Kept for backward compatibility
}) => {
	// Function to render cell content based on column definition
	const renderCell = (item, column) => {
		// If the column has a render function, use it
		if (column.render) {
			return column.render(item)
		}

		// Otherwise, display the raw value
		return item[column.key]
	}

	// Determine the appropriate style for the table container
	const tableStyle =
		height === "auto"
			? { height: "100%" }
			: { height: typeof height === "number" ? `${height}px` : height }

	return (
		<div className="w-full h-full overflow-auto" style={tableStyle}>
			{/* Title if provided */}
			{title && (
				<div className="px-2 py-2">
					<h3 className="text-lg font-medium text-gray-900 text-left">{title}</h3>
				</div>
			)}

			<table className="min-w-full">
				{/* Table header */}
				<thead className="bg-gray-50 sticky top-0 z-10">
					<tr>
						{columns.map((column, idx) => (
							<th
								key={column.key || idx}
								scope="col"
								className={`py-3 px-3 text-left text-sm font-semibold text-gray-900 ${
									idx === 0 ? "pl-4 pr-3" : ""
								} ${column.isNumeric ? "text-right" : "text-left"}`}
							>
								{column.label}
							</th>
						))}
					</tr>
				</thead>

				{/* Table body */}
				<tbody className="divide-y divide-gray-200">
					{data.length > 0 ? (
						data.map((item, rowIdx) => (
							<tr
								key={item.id || rowIdx}
								className={onRowClick ? "cursor-pointer hover:bg-gray-50" : ""}
								onClick={onRowClick ? () => onRowClick(item) : undefined}
							>
								{columns.map((column, colIdx) => (
									<td
										key={`${rowIdx}-${column.key || colIdx}`}
										className={`py-3 px-3 text-sm ${
											colIdx === 0
												? "pl-4 pr-3 font-medium text-gray-900"
												: "text-gray-500"
										} ${column.isNumeric ? "text-right" : "text-left"}`}
									>
										{renderCell(item, column)}
									</td>
								))}
							</tr>
						))
					) : (
						<tr>
							<td
								colSpan={columns.length}
								className="px-6 py-4 text-center text-sm text-gray-500"
							>
								{emptyMessage}
							</td>
						</tr>
					)}
				</tbody>
			</table>

			{/* Footer with actions has been removed - now handled by custom footer in BlankCard */}
		</div>
	)
}

/**
 * Creates a percentage badge with color based on the value
 *
 * @param {number} value - The percentage value to display
 * @param {boolean} isHigherBetter - Whether higher values are better
 * @returns {JSX.Element}
 */
export const PercentageBadge = ({ value, isHigherBetter = true }) => {
	// Determine color based on value and whether higher is better
	let colorClass
	const absValue = Math.abs(value)
	const isPositive = value > 0

	if (isHigherBetter) {
		colorClass = isPositive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
	} else {
		colorClass = isPositive ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"
	}

	// For small variations, use neutral color
	if (absValue < 5) {
		colorClass = "bg-gray-100 text-gray-800"
	}

	return (
		<span
			className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}
		>
			{value > 0 ? "+" : ""}
			{value}%
		</span>
	)
}

export default Table
